import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PageableRequest } from 'src/app/admin-api';
import { PageContent } from 'src/app/components/table';
import { StorageService } from 'src/app/services/auth/storage.service';
import { environment } from 'src/environments/environment';

export interface MarketingGlampointsAction {
  id: number;
  name: string;
  description?: string;
  points: number;
  status: string; // ACTIVE | INACTIVE
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface MarketingGlampointsActionRequest {
  name: string;
  description?: string;
  points: number;
  status?: string;
}

export type MarketingGlampointsCreditStatus =
  | 'SUCCESS'
  | 'ELIGIBLE'
  | 'DUPLICATE'
  | 'NOT_FOUND'
  | 'NO_GLAM_SUBSCRIPTION'
  | 'ERROR';

export interface MarketingGlampointsCreditResult {
  email: string;
  status: MarketingGlampointsCreditStatus;
  message: string;
  personId?: number;
  rewardId?: number;
}

export interface MarketingGlampointsBatchResponse {
  total: number;
  /** No credit é o nº de creditados; no preview é o nº de elegíveis. */
  succeeded: number;
  duplicated: number;
  notFound: number;
  noGlamSubscription: number;
  failed: number;
  results: MarketingGlampointsCreditResult[];
}

/** Linha da tabela de auditoria (histórico) — espelha a entidade Kpi MarketingGlampointsCredit. */
export interface MarketingGlampointsCreditRow {
  id: number;
  actionId: number;
  personId: number;
  email: string;
  points: number;
  campaign: string;
  subtitle?: string;
  rewardId?: number;
  status: string;
  resultMessage?: string;
  createdBy: string;
  createdByUserId?: number;
  createdAt: string;
}

interface ResponseB4A<T> {
  result: T;
}

/**
 * Cliente para os endpoints /marketing/glampoints do admin-api.
 *
 * Escrito à mão (mesmo padrão de MgmConfigService) porque os métodos ainda não
 * existem no client gerado em src/app/admin-api. Pode ser substituído pelo
 * serviço gerado após `npm run build:admin-api:stage`.
 */
@Injectable({ providedIn: 'root' })
export class MarketingGlampointsService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/marketing/glampoints`;
  private actionsUrl = `${this.baseUrl}/actions`;

  private authHeaders(): HttpHeaders {
    const headers = new HttpHeaders();
    if (StorageService.userLogged && StorageService.token) {
      return headers.set('Authorization', `Bearer ${StorageService.token}`);
    }
    return headers;
  }

  // ------- Ações -------

  listActions(): Observable<MarketingGlampointsAction[]> {
    return this.http
      .get<ResponseB4A<MarketingGlampointsAction[]>>(this.actionsUrl, {
        headers: this.authHeaders()
      })
      .pipe(map((r) => r.result ?? []));
  }

  getAction(actionId: number): Observable<MarketingGlampointsAction> {
    return this.http
      .get<ResponseB4A<MarketingGlampointsAction>>(`${this.actionsUrl}/${actionId}`, {
        headers: this.authHeaders()
      })
      .pipe(map((r) => r.result));
  }

  createAction(
    body: MarketingGlampointsActionRequest
  ): Observable<MarketingGlampointsAction> {
    return this.http
      .post<ResponseB4A<MarketingGlampointsAction>>(this.actionsUrl, body, {
        headers: this.authHeaders()
      })
      .pipe(map((r) => r.result));
  }

  updateAction(
    actionId: number,
    body: MarketingGlampointsActionRequest
  ): Observable<MarketingGlampointsAction> {
    return this.http
      .put<ResponseB4A<MarketingGlampointsAction>>(`${this.actionsUrl}/${actionId}`, body, {
        headers: this.authHeaders()
      })
      .pipe(map((r) => r.result));
  }

  // ------- Gerar (creditar em lote) -------

  /** Dry-run no backend: valida os e-mails (existe? assinatura ativa? duplicado?) sem creditar. */
  previewCredit(
    actionId: number,
    body: { emails: string[] }
  ): Observable<MarketingGlampointsBatchResponse> {
    return this.http
      .post<ResponseB4A<MarketingGlampointsBatchResponse>>(
        `${this.actionsUrl}/${actionId}/credit/preview`,
        body,
        { headers: this.authHeaders() }
      )
      .pipe(map((r) => r.result));
  }

  creditAction(
    actionId: number,
    body: { emails: string[] }
  ): Observable<MarketingGlampointsBatchResponse> {
    return this.http
      .post<ResponseB4A<MarketingGlampointsBatchResponse>>(
        `${this.actionsUrl}/${actionId}/credit`,
        body,
        { headers: this.authHeaders() }
      )
      .pipe(map((r) => r.result));
  }

  // ------- Histórico (o front escopa por ActionId via fixedFilters) -------

  findHistory(
    request: PageableRequest
  ): Observable<ResponseB4A<PageContent<MarketingGlampointsCreditRow>>> {
    return this.http.put<ResponseB4A<PageContent<MarketingGlampointsCreditRow>>>(
      `${this.baseUrl}/history`,
      request,
      { headers: this.authHeaders() }
    );
  }
}
